import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Warning, SignOut, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useState } from "react";

/**
 * Persistent banner shown on every page while an admin is impersonating a user.
 * Cannot be dismissed. Exposes two exit paths:
 *   - "End support session" ends impersonation and lands on /admin/usuarios/:target_id
 *   - "Return to Admin dashboard" ends impersonation and lands on /admin
 */
export default function ImpersonationBanner() {
    const { impersonation, isImpersonating, stopImpersonation } = useAuth();
    const nav = useNavigate();
    const [busy, setBusy] = useState(false);
    if (!isImpersonating || !impersonation) return null;

    const exit = async (to) => {
        setBusy(true);
        try {
            await stopImpersonation();
            nav(to);
            toast.success("Support session ended");
        } catch (e) {
            toast.error("Failed to end the support session");
        } finally { setBusy(false); }
    };

    return (
        <div
            className="sticky top-0 z-[70] bg-[#E8A33A] text-white shadow-md"
            data-testid="impersonation-banner"
        >
            <div className="container-page py-2.5 flex flex-wrap items-center gap-3 justify-between">
                <div className="flex items-center gap-3 min-w-0">
                    <Warning size={20} weight="fill" className="shrink-0" />
                    <div className="min-w-0">
                        <div className="font-bold text-sm truncate" data-testid="impersonation-banner-text">
                            You are viewing CampoDirecto as {impersonation.target_name || impersonation.target_email}
                        </div>
                        <div className="text-xs opacity-90 truncate">
                            Role: {impersonation.target_role} · This action is logged for security.
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <button
                        onClick={() => exit(`/admin/usuarios/${impersonation.target_id}`)}
                        disabled={busy}
                        className="h-9 rounded-full bg-white/15 hover:bg-white/25 px-3 text-xs font-bold inline-flex items-center gap-1"
                        data-testid="impersonation-exit-support"
                    >
                        <SignOut size={14} weight="bold" /> End support session
                    </button>
                    <button
                        onClick={() => exit("/admin")}
                        disabled={busy}
                        className="h-9 rounded-full bg-white text-[#B88429] hover:bg-white/90 px-3 text-xs font-bold inline-flex items-center gap-1"
                        data-testid="impersonation-back-to-admin"
                    >
                        <ArrowLeft size={14} weight="bold" /> Return to Administrator dashboard
                    </button>
                </div>
            </div>
        </div>
    );
}
